import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    agreeTerms: false,
    enableMfa: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register, loading, error } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      return;
    }

    const success = await register(formData);
    if (success) {
      navigate('/verify-email');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Lobster&display=swap"
        rel="stylesheet"
      />

      <div style={{
        minHeight: '100vh',
        background: theme === 'dark'
          ? '#1a1a1a'
          : 'rgba(177,187,214,0.60)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        position: 'relative'
      }}>
        {/* Theme Toggle Button */}
        <button
          onClick={toggleTheme}
          style={{
            position: 'absolute',
            top: '1.5rem',
            right: '1.5rem',
            background: 'none',
            border: 'none',
            borderRadius: '0.375rem',
            width: '40px',
            height: '40px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            color: theme === 'dark' ? '#ffffff' : '#2d333a',
            transition: 'all 0.15s ease'
          }}
          title="Toggle theme"
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2a' : '#f5f5f5';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>

      <div style={{
        width: '100%',
        maxWidth: '500px'
      }}>
        <div style={{
          backgroundColor: theme === 'dark' ? '#2d333a' : '#ffffff',
          borderRadius: '1rem',
          boxShadow: theme === 'dark'
            ? '0 20px 40px rgba(0, 0, 0, 0.3)'
            : '0 20px 40px rgba(0, 0, 0, 0.1)',
          padding: '3rem',
          border: `1px solid ${theme === 'dark' ? '#404040' : 'rgba(39,71,151,0.2)'}`
        }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: 800,
              color: '#274797',
              marginBottom: '0.5rem',
              fontFamily: "'Lobster', cursive",
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}>
              SpaarxSense
            </h1>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: theme === 'dark' ? '#ffffff' : '#2d333a',
              marginBottom: '0.5rem'
            }}>
              Create Account
            </h2>
            <p style={{
              color: theme === 'dark' ? '#9ca3af' : '#6e7681',
              fontSize: '1rem'
            }}>
              Join SpaarxSense today
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{ marginBottom: '1.5rem' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: theme === 'dark' ? '#ffffff' : '#2d333a',
                  marginBottom: '0.5rem'
                }}>
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  placeholder="John"
                  style={{
                    width: '100%',
                    padding: '1rem',
                    border: `1px solid ${theme === 'dark' ? '#404040' : '#e5e5e5'}`,
                    borderRadius: '0.5rem',
                    backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
                    color: theme === 'dark' ? '#ffffff' : '#2d333a',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: theme === 'dark' ? '#ffffff' : '#2d333a',
                  marginBottom: '0.5rem'
                }}>
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  placeholder="Doe"
                  style={{
                    width: '100%',
                    padding: '1rem',
                    border: `1px solid ${theme === 'dark' ? '#404040' : '#e5e5e5'}`,
                    borderRadius: '0.5rem',
                    backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
                    color: theme === 'dark' ? '#ffffff' : '#2d333a',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                marginBottom: '0.5rem'
              }}>
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@example.com"
                style={{
                  width: '100%',
                  padding: '1rem',
                  border: `1px solid ${theme === 'dark' ? '#404040' : '#e5e5e5'}`,
                  borderRadius: '0.5rem',
                  backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
                  color: theme === 'dark' ? '#ffffff' : '#2d333a',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                marginBottom: '0.5rem'
              }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Create a strong password"
                  style={{
                    width: '100%',
                    padding: '1rem 3rem 1rem 1rem',
                    border: `1px solid ${theme === 'dark' ? '#404040' : '#e5e5e5'}`,
                    borderRadius: '0.5rem',
                    backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
                    color: theme === 'dark' ? '#ffffff' : '#2d333a',
                    fontSize: '1rem'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#8e9297',
                    cursor: 'pointer'
                  }}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                marginBottom: '0.5rem'
              }}>
                Confirm Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="Confirm your password"
                  style={{
                    width: '100%',
                    padding: '1rem 3rem 1rem 1rem',
                    border: `1px solid ${theme === 'dark' ? '#404040' : '#e5e5e5'}`,
                    borderRadius: '0.5rem',
                    backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
                    color: theme === 'dark' ? '#ffffff' : '#2d333a',
                    fontSize: '1rem'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#8e9297',
                    cursor: 'pointer'
                  }}
                >
                  {showConfirmPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: theme === 'dark' ? '#9ca3af' : '#6e7681',
                marginBottom: '1rem'
              }}>
                <input
                  type="checkbox"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleChange}
                  required
                  style={{ marginRight: '0.5rem' }}
                />
                I agree to the Terms of Service and Privacy Policy
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: theme === 'dark' ? '#9ca3af' : '#6e7681'
              }}>
                <input
                  type="checkbox"
                  name="enableMfa"
                  checked={formData.enableMfa}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem' }}
                />
                Enable Multi-Factor Authentication (Recommended)
              </label>
            </div>

            {error && (
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '0.5rem',
                color: '#dc2626',
                fontSize: '0.875rem',
                marginBottom: '1.5rem'
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !formData.agreeTerms}
              style={{
                width: '100%',
                padding: '1rem 1.5rem',
                border: 'none',
                borderRadius: '0.5rem',
                backgroundColor: loading || !formData.agreeTerms ? '#8e9297' : '#274797',
                color: 'white',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: loading || !formData.agreeTerms ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? (
                <>
                  <div className="spinner" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div style={{ textAlign: 'center' }}>
            <p style={{
              color: theme === 'dark' ? '#9ca3af' : '#6e7681',
              fontSize: '0.875rem'
            }}>
              Already have an account?{' '}
              <Link to="/login" style={{
                color: '#274797',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'color 0.15s ease'
              }}>
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default RegisterPage;
